/**
 * HTTP 상태·에러 판별 유틸.
 *
 * @description 종전에는 여기에 axios 인스턴스와 인터셉터가 있었다(enter-launcher-api 연동).
 *  백엔드가 Supabase 하나로 합쳐지며 supabase-js 가 통신을 전담해 axios 계층 자체가 사라졌다 —
 *  남은 것은 에러 처리기가 상태 코드를 해석할 때 쓰는 상수와 판별 함수뿐이다.
 */

export const HTTP_STATUS_ERROR_MIN = 400;
export const HTTP_STATUS_UNAUTHORIZED = 401;
export const HTTP_STATUS_TOO_MANY_REQUESTS = 429;
export const HTTP_STATUS_SERVER_ERROR_MIN = 500;

/** Supabase(PostgrestError 등)와 fetch 계열 에러가 공통으로 갖는 최소 형태. */
interface HttpLikeError {
  status?: number;
  message?: string;
  name?: string;
}

function asHttpLike(error: unknown): HttpLikeError | null {
  return typeof error === 'object' && error !== null ? (error as HttpLikeError) : null;
}

/**
 * 상태 코드를 가진 에러인지.
 * @description axios 전용 판별을 대체한다. Supabase 에러는 `status` 를 직접 들고 있어 그대로 읽는다.
 */
export function isApiError(error: unknown): boolean {
  const candidate = asHttpLike(error);
  return Boolean(candidate && typeof candidate.status === 'number');
}

/** 에러에서 HTTP 상태를 꺼낸다. 없으면 undefined. */
export function getErrorStatus(error: unknown): number | undefined {
  return asHttpLike(error)?.status;
}

/** 화면 이탈 등으로 취소된 요청인지 — 취소는 사용자에게 알릴 실패가 아니다. */
export function isCanceledError(error: unknown): boolean {
  const candidate = asHttpLike(error);
  if (!candidate) return false;
  return candidate.name === 'AbortError' || /aborted|cancel/i.test(candidate.message ?? '');
}
